/**
 * Platform-wide diagnostic recorder (admin/super_admin only, opt-in).
 *
 * Captures console output, network request metadata, and a redacted storage
 * snapshot into in-memory ring buffers while explicitly started. PHI safety is
 * the governing constraint:
 *  - request/response bodies are NEVER captured unless the URL matches the
 *    tiny BODY_CAPTURE_ALLOW list (metadata-only is the default, not opt-out);
 *  - headers are never captured at all (so Authorization/cookies can't leak);
 *  - URL paths have IDs/emails redacted and query strings stripped;
 *  - captured text is scrubbed of bearer tokens and JWT-shaped strings;
 *  - buffers live only in memory, so closing the tab discards everything.
 */

export type DiagnosticConsoleEntry = {
  ts: string;
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
};

export type DiagnosticNetworkEntry = {
  ts: string;
  kind: 'fetch' | 'xhr';
  method: string;
  path: string;
  status: number | null;
  durationMs: number;
  error: string | null;
  responseBody: string | null;
};

export type DiagnosticSessionReport = {
  startedAt: string;
  endedAt: string;
  userId: string | null;
  role: string | null;
  appVersion: string;
  userAgent: string;
  consoleEntries: DiagnosticConsoleEntry[];
  networkEntries: DiagnosticNetworkEntry[];
  storageSnapshot: Record<string, string>;
};

export type DiagnosticRecorderState = {
  active: boolean;
  startedAt: string | null;
  consoleCount: number;
  networkCount: number;
};

const MAX_CONSOLE_ENTRIES = 2_000;
const MAX_NETWORK_ENTRIES = 1_000;
const MAX_MESSAGE_CHARS = 2_000;
const MAX_BODY_CHARS = 10_000;
const MAX_STORAGE_VALUE_CHARS = 500;
// Backend accepts up to the express body limit (1MB); trim exports below it.
const MAX_EXPORT_BYTES = 800_000;

// Endpoints where response-body capture is safe (no PHI). Everything else is
// metadata-only. Keep this list short and boring.
const BODY_CAPTURE_ALLOW: RegExp[] = [/^\/api\/health$/, /^\/api\/v2\/health$/];

// Storage keys are captured only when they match this prefix AND don't look
// credential/auth related.
const STORAGE_KEY_ALLOW_PREFIX = 'jema.';
const SENSITIVE_KEY_PATTERN = /token|auth|credential|secret|session|password|key/i;

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_ID_SEGMENT = /^\d{5,}$/;
const EMAIL_PATTERN = /[^\s/@]+@[^\s/@]+\.[^\s/@]+/g;
const BEARER_PATTERN = /Bearer\s+[A-Za-z0-9\-_.~+/]+=*/g;
const JWT_PATTERN = /eyJ[A-Za-z0-9\-_]{8,}\.[A-Za-z0-9\-_]{8,}\.[A-Za-z0-9\-_]+/g;
const SECRET_JSON_FIELD =
  /"(authorization|access_token|refresh_token|token|api_?key|password|secret|cookie|sip_password|credential[a-z_]*)"\s*:\s*"[^"]*"/gi;

export const scrubSensitiveText = (input: string): string =>
  input
    .replace(BEARER_PATTERN, 'Bearer [redacted]')
    .replace(JWT_PATTERN, '[redacted-jwt]')
    .replace(SECRET_JSON_FIELD, '"$1":"[redacted]"')
    .replace(EMAIL_PATTERN, '[redacted-email]');

/** Strips origin/query/hash and replaces id-like path segments. */
export const redactUrlPath = (rawUrl: string): string => {
  let path = rawUrl;
  try {
    path = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'http://local')
      .pathname;
  } catch {
    path = rawUrl.split('?')[0]?.split('#')[0] ?? rawUrl;
  }
  return path
    .split('/')
    .map(segment =>
      UUID_SEGMENT.test(segment) || NUMERIC_ID_SEGMENT.test(segment) || segment.includes('@')
        ? ':id'
        : segment
    )
    .join('/');
};

export const isBodyCaptureAllowed = (redactedPath: string): boolean =>
  BODY_CAPTURE_ALLOW.some(pattern => pattern.test(redactedPath));

const truncate = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, max)}…[truncated]` : value;

const stringifyArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

type Listener = () => void;

class DiagnosticRecorder {
  private consoleEntries: DiagnosticConsoleEntry[] = [];
  private networkEntries: DiagnosticNetworkEntry[] = [];
  private startedAt: string | null = null;
  private userId: string | null = null;
  private role: string | null = null;
  private listeners = new Set<Listener>();
  private restoreHooks: Array<() => void> = [];
  private stateSnapshot: DiagnosticRecorderState = {
    active: false,
    startedAt: null,
    consoleCount: 0,
    networkCount: 0,
  };

  get isActive(): boolean {
    return this.startedAt !== null;
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): DiagnosticRecorderState => this.stateSnapshot;

  private notify() {
    this.stateSnapshot = {
      active: this.isActive,
      startedAt: this.startedAt,
      consoleCount: this.consoleEntries.length,
      networkCount: this.networkEntries.length,
    };
    this.listeners.forEach(listener => listener());
  }

  start(context: { userId: string | null; role: string | null }) {
    if (this.isActive) return;
    this.consoleEntries = [];
    this.networkEntries = [];
    this.startedAt = new Date().toISOString();
    this.userId = context.userId;
    this.role = context.role;
    this.installConsoleHooks();
    this.installFetchHook();
    this.installXhrHook();
    this.notify();
  }

  stop() {
    if (!this.isActive) return;
    this.restoreHooks.forEach(restore => restore());
    this.restoreHooks = [];
    this.startedAt = null;
    this.notify();
  }

  /** Stops capture and discards all buffered data. */
  discard() {
    this.stop();
    this.consoleEntries = [];
    this.networkEntries = [];
    this.notify();
  }

  private pushConsole(entry: DiagnosticConsoleEntry) {
    this.consoleEntries.push(entry);
    if (this.consoleEntries.length > MAX_CONSOLE_ENTRIES) this.consoleEntries.shift();
    this.notify();
  }

  private pushNetwork(entry: DiagnosticNetworkEntry) {
    this.networkEntries.push(entry);
    if (this.networkEntries.length > MAX_NETWORK_ENTRIES) this.networkEntries.shift();
    this.notify();
  }

  private installConsoleHooks() {
    /* eslint-disable no-console -- this module wraps the console on purpose */
    (['log', 'info', 'warn', 'error'] as const).forEach(level => {
      const original = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        this.pushConsole({
          ts: new Date().toISOString(),
          level,
          message: truncate(
            scrubSensitiveText(args.map(stringifyArg).join(' ')),
            MAX_MESSAGE_CHARS
          ),
        });
        original(...args);
      };
      this.restoreHooks.push(() => {
        console[level] = original;
      });
    });
    /* eslint-enable no-console */
  }

  private installFetchHook() {
    if (typeof window === 'undefined') return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const method = (
        init?.method ?? (input instanceof Request ? input.method : 'GET')
      ).toUpperCase();
      const rawUrl =
        typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      const path = redactUrlPath(rawUrl);
      const startedAt = performance.now();
      try {
        const response = await originalFetch(input, init);
        let responseBody: string | null = null;
        if (isBodyCaptureAllowed(path)) {
          try {
            const text = await response.clone().text();
            responseBody = truncate(scrubSensitiveText(text), MAX_BODY_CHARS);
          } catch {
            responseBody = null;
          }
        }
        this.pushNetwork({
          ts: new Date().toISOString(),
          kind: 'fetch',
          method,
          path,
          status: response.status,
          durationMs: Math.round(performance.now() - startedAt),
          error: null,
          responseBody,
        });
        return response;
      } catch (error) {
        this.pushNetwork({
          ts: new Date().toISOString(),
          kind: 'fetch',
          method,
          path,
          status: null,
          durationMs: Math.round(performance.now() - startedAt),
          error: scrubSensitiveText(error instanceof Error ? error.message : String(error)),
          responseBody: null,
        });
        throw error;
      }
    };
    this.restoreHooks.push(() => {
      window.fetch = originalFetch;
    });
  }

  private installXhrHook() {
    if (typeof XMLHttpRequest === 'undefined') return;
    const pushNetwork = (entry: DiagnosticNetworkEntry) => this.pushNetwork(entry);
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest & { __diag?: { method: string; path: string } },
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      this.__diag = { method: method.toUpperCase(), path: redactUrlPath(String(url)) };
      return originalOpen.apply(this, [method, url, ...rest] as Parameters<
        typeof XMLHttpRequest.prototype.open
      >);
    } as typeof XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.send = function (
      this: XMLHttpRequest & { __diag?: { method: string; path: string } },
      body?: Document | XMLHttpRequestBodyInit | null
    ) {
      const meta = this.__diag;
      if (meta) {
        const startedAt = performance.now();
        this.addEventListener('loadend', () => {
          pushNetwork({
            ts: new Date().toISOString(),
            kind: 'xhr',
            method: meta.method,
            path: meta.path,
            status: this.status || null,
            durationMs: Math.round(performance.now() - startedAt),
            error: this.status === 0 ? 'network error or aborted' : null,
            responseBody: null,
          });
        });
      }
      return originalSend.call(this, body ?? null);
    };
    this.restoreHooks.push(() => {
      XMLHttpRequest.prototype.open = originalOpen;
      XMLHttpRequest.prototype.send = originalSend;
    });
  }

  private snapshotStorage(): Record<string, string> {
    const snapshot: Record<string, string> = {};
    if (typeof window === 'undefined') return snapshot;
    ([window.localStorage, window.sessionStorage] as const).forEach((storage, index) => {
      const prefix = index === 0 ? 'local:' : 'session:';
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key) continue;
        if (!key.startsWith(STORAGE_KEY_ALLOW_PREFIX)) continue;
        if (SENSITIVE_KEY_PATTERN.test(key)) continue;
        const value = storage.getItem(key) ?? '';
        snapshot[`${prefix}${key}`] = truncate(scrubSensitiveText(value), MAX_STORAGE_VALUE_CHARS);
      }
    });
    return snapshot;
  }

  exportSession(): DiagnosticSessionReport {
    const report: DiagnosticSessionReport = {
      startedAt: this.startedAt ?? new Date().toISOString(),
      endedAt: new Date().toISOString(),
      userId: this.userId,
      role: this.role,
      appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      consoleEntries: [...this.consoleEntries],
      networkEntries: [...this.networkEntries],
      storageSnapshot: this.snapshotStorage(),
    };
    // Keep the payload under the backend body limit by dropping oldest entries.
    while (
      JSON.stringify(report).length > MAX_EXPORT_BYTES &&
      (report.consoleEntries.length > 0 || report.networkEntries.length > 0)
    ) {
      report.consoleEntries.splice(0, Math.max(1, Math.floor(report.consoleEntries.length / 4)));
      report.networkEntries.splice(0, Math.max(1, Math.floor(report.networkEntries.length / 4)));
    }
    return report;
  }
}

export const diagnosticRecorder = new DiagnosticRecorder();
